/* FoodFor cart integrations
 * Adds a stable styling layer around dynamically injected Monk cart offers.
 * Product eligibility, variants, inventory and prices remain owned by Shopify/Monk.
 */
(() => {
  const ROOT_SELECTOR = '#mr-div-embedded-cp-any-drawer';
  const ADD_PATTERN = /add to cart|hinzuf(?:ü|u)gen/i;
  const ADDED_PATTERN = /added|hinzugef(?:ü|u)gt/i;
  const DEFAULTS = {
    de: {
      title: 'PASST DAZU',
      add: '+ HINZUFÜGEN',
      added: 'HINZUGEFÜGT',
      bundle: 'BUNDLE'
    },
    en: {
      title: 'YOU MAY ALSO LIKE',
      add: '+ ADD TO CART',
      added: 'ADDED',
      bundle: 'BUNDLE'
    }
  };

  const directText = (element) => Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent.trim())
    .filter(Boolean)
    .join(' ');

  const languageCopy = (root) => {
    const language = (document.documentElement.lang || 'de').toLowerCase().startsWith('de') ? 'de' : 'en';
    const defaults = DEFAULTS[language];
    return {
      title: root.dataset.foodforTitle || defaults.title,
      add: root.dataset.foodforAddLabel || defaults.add,
      added: root.dataset.foodforAddedLabel || defaults.added,
      bundle: root.dataset.foodforBundleLabel || defaults.bundle
    };
  };

  const monkImageOverrides = () => {
    const script = document.querySelector('[data-foodfor-monk-image-overrides]');
    if (!script?.textContent.trim()) return [];

    try {
      return JSON.parse(script.textContent)
        .filter((entry) => entry?.src && (entry?.handle || entry?.title));
    } catch (error) {
      console.error('FoodFor: Monk image overrides could not be read.', error);
      return [];
    }
  };

  const monkProductHandle = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    try {
      const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_error) {
      return '';
    }
  };

  const monkImageOverrideForCard = (card, overrides) => {
    const handle = monkProductHandle(card);
    const byHandle = handle && overrides.find((entry) => String(entry.handle) === handle);
    if (byHandle) return String(byHandle.src);

    const cardCopy = card.textContent.trim().toLocaleLowerCase();
    const byTitle = overrides.find((entry) => entry.title && cardCopy.includes(String(entry.title).trim().toLocaleLowerCase()));
    return byTitle ? String(byTitle.src) : '';
  };

  const findCard = (control, root, allControls) => {
    let element = control.parentElement;
    let card = element;

    while (element && element !== root) {
      const controlsInside = allControls.filter((item) => element.contains(item));
      if (controlsInside.length === 1 && element.querySelector('img')) card = element;

      const parent = element.parentElement;
      if (!parent || parent === root) break;
      const controlsInParent = allControls.filter((item) => parent.contains(item));
      if (controlsInParent.length > 1) break;
      element = parent;
    }

    return card;
  };

  const lowestCommonParent = (elements, root) => {
    if (elements.length < 2) return elements[0]?.parentElement || null;
    let candidate = elements[0].parentElement;
    while (candidate && candidate !== root) {
      if (elements.every((element) => candidate.contains(element))) return candidate;
      candidate = candidate.parentElement;
    }
    return null;
  };

  const normalizeMonkOffer = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.childElementCount === 0) return;

    const copy = languageCopy(root);
    const imageOverrides = monkImageOverrides();
    root.classList.add('foodfor-cart__monk-ready');

    const textElements = Array.from(root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,div'));
    const title = textElements.find((element) => {
      const text = directText(element);
      return /people also bought|passt dazu|you may also like/i.test(text);
    });
    if (title) {
      if (title.textContent.trim() !== copy.title) title.textContent = copy.title;
      title.classList.add('foodfor-cart__monk-title');
    }

    const controls = Array.from(root.querySelectorAll('button,input[type="submit"],[role="button"]'))
      .filter((element) => ADD_PATTERN.test(element.value || element.textContent || '') || ADDED_PATTERN.test(element.value || element.textContent || ''));

    const cards = [];
    controls.forEach((control) => {
      const isAdded = ADDED_PATTERN.test(control.value || control.textContent || '');
      const label = isAdded ? copy.added : copy.add;
      if (control.tagName === 'INPUT') {
        if (control.value !== label) control.value = label;
      } else if (control.textContent.trim() !== label) {
        control.textContent = label;
      }
      control.classList.add('foodfor-cart__monk-add');

      const card = findCard(control, root, controls);
      if (!card || cards.includes(card)) return;
      cards.push(card);
      card.classList.add('foodfor-cart__monk-card');

      const imageOverride = monkImageOverrideForCard(card, imageOverrides);
      card.querySelectorAll('img').forEach((image) => {
        image.classList.add('foodfor-cart__monk-image');
        image.parentElement?.classList.add('foodfor-cart__monk-image-frame');
        if (!imageOverride) return;
        image.src = imageOverride;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      });
      card.querySelectorAll('select').forEach((select) => select.classList.add('foodfor-cart__monk-select'));

      const titleLink = Array.from(card.querySelectorAll('a,h1,h2,h3,h4,h5,h6'))
        .find((element) => element.textContent.trim() && !element.querySelector('img'));
      if (titleLink) titleLink.classList.add('foodfor-cart__monk-product-title');

      const price = Array.from(card.querySelectorAll('span,p,div'))
        .find((element) => element.childElementCount === 0 && /(?:CHF|EUR|€|Fr\.)\s*\d|\d[\d.,'\s]*\s*(?:CHF|EUR|€)/i.test(element.textContent));
      if (price) price.classList.add('foodfor-cart__monk-price');

      const bundleLink = card.querySelector('a[href*="bundle" i]');
      if ((/bundle/i.test(card.textContent) || bundleLink) && !card.querySelector('.foodfor-cart__monk-badge')) {
        const badge = document.createElement('span');
        badge.className = 'foodfor-cart__monk-badge';
        badge.textContent = copy.bundle;
        card.prepend(badge);
      }
    });

    const grid = lowestCommonParent(cards, root);
    if (grid) grid.classList.add('foodfor-cart__monk-grid');
  };

  const cartChangeUrl = () => {
    if (window.theme?.routes?.cart_change_url) return window.theme.routes.cart_change_url;
    return `${window.Shopify?.routes?.root || '/'}cart/change.js`;
  };

  const cartDrawerContext = () => {
    const drawer = document.querySelector('#CartDrawer');
    return {
      drawer,
      sectionId: drawer?.dataset.sectionId || drawer?.getAttribute('data-section-id') || ''
    };
  };

  const cartStateUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;

  const fetchCartState = async () => {
    const response = await fetch(cartStateUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart could not be loaded');
    return cart;
  };

  const changeSellingPlan = async ({ key, quantity, sellingPlan }) => {
    const body = {
      id: key,
      quantity: Number(quantity),
      selling_plan: sellingPlan ? Number(sellingPlan) : null
    };

    const response = await fetch(cartChangeUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');
    return cart;
  };

  const updateOffersPanel = (cart) => {
    const drawer = document.querySelector('#CartDrawer');
    const sectionId = drawer?.dataset.sectionId;
    const sectionHtml = sectionId && cart.sections?.[sectionId];
    if (!sectionHtml) return;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const escapedSectionId = window.CSS?.escape ? CSS.escape(sectionId) : sectionId;
    const current = document.querySelector(`#CartOffers-${escapedSectionId}`);
    const updated = parsed.querySelector(`#CartOffers-${escapedSectionId}`);
    if (current && updated) current.innerHTML = updated.innerHTML;
  };

  const PRESERVED_MONK_SELECTORS = [
    '#mr-div-embedded-cp-any-drawer',
    '#mr-div-embedded-cp-cvb-drawer',
    '#mr-div-embedded-cp-freegift_manual-drawer'
  ];

  const renderCartDrawerSection = (sectionHtml) => {
    const { drawer, sectionId } = cartDrawerContext();
    if (!drawer || !sectionId || !sectionHtml) return false;

    const activePanelId = drawer.querySelector('.drawer__panel[open]')?.id || `MiniCart-${sectionId}`;
    const preservedMonkNodes = PRESERVED_MONK_SELECTORS
      .map((selector) => [selector, drawer.querySelector(selector)])
      .filter((entry) => entry[1]);
    const scrollPositions = new Map();
    drawer.querySelectorAll('.drawer__panel[id]').forEach((panel) => {
      const scrollable = panel.querySelector('.drawer__scrollable');
      if (scrollable) scrollPositions.set(panel.id, scrollable.scrollTop);
    });

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    let replacedPanels = 0;
    [`MiniCart-${sectionId}`, `CartOffers-${sectionId}`].forEach((panelId) => {
      const current = document.getElementById(panelId);
      const updated = parsed.getElementById(panelId);
      if (!current || !updated) return;
      current.innerHTML = updated.innerHTML;
      replacedPanels += 1;
    });
    if (!replacedPanels) return false;

    const currentCount = drawer.querySelector('cart-count');
    const updatedCount = parsed.querySelector('#CartDrawer cart-count');
    if (currentCount && updatedCount) {
      currentCount.innerHTML = updatedCount.innerHTML;
      currentCount.hidden = updatedCount.hidden;
      const label = updatedCount.getAttribute('aria-label');
      if (label) currentCount.setAttribute('aria-label', label);
    }

    preservedMonkNodes.forEach(([selector, node]) => {
      const placeholder = drawer.querySelector(selector);
      if (placeholder && placeholder !== node) placeholder.replaceWith(node);
    });

    const activePanel = document.getElementById(activePanelId) || document.getElementById(`MiniCart-${sectionId}`);
    drawer.querySelectorAll('.drawer__panel[id]').forEach((panel) => {
      panel.toggleAttribute('open', panel === activePanel);
    });
    drawer.querySelectorAll('[is="tab-list"] [aria-controls]').forEach((control) => {
      control.setAttribute('aria-expanded', String(control.getAttribute('aria-controls') === activePanel?.id));
    });
    scrollPositions.forEach((scrollTop, panelId) => {
      const scrollable = document.getElementById(panelId)?.querySelector('.drawer__scrollable');
      if (scrollable) scrollable.scrollTop = scrollTop;
    });

    scheduleNormalization();
    return true;
  };

  const refreshCartDrawer = async () => {
    const { drawer, sectionId } = cartDrawerContext();
    if (!drawer || !sectionId) throw new Error('Cart drawer section is unavailable');

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('_foodfor_cart', String(Date.now()));
    const response = await fetch(pageUrl.href, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'Cache-Control': 'no-cache' }
    });
    if (!response.ok) throw new Error(`Cart drawer refresh failed (${response.status})`);
    if (!renderCartDrawerSection(await response.text())) {
      throw new Error('Cart drawer refresh returned no matching panels');
    }
  };

  const reloadWithCartDrawerOpen = () => {
    const activePanel = document.querySelector('#CartDrawer .drawer__panel[open]');
    const reopenTab = activePanel?.id?.startsWith('CartOffers-') ? 'offers' : 'cart';
    try {
      window.sessionStorage.setItem('foodfor-cart-reopen-tab', reopenTab);
    } catch (_error) {
      // A blocked sessionStorage must never prevent the reliable reload fallback.
    }
    window.location.reload();
    return new Promise(() => {});
  };

  let offersRefreshController;
  const refreshOffersPanel = async () => {
    const drawer = document.querySelector('#CartDrawer');
    const sectionId = drawer?.dataset.sectionId;
    if (!sectionId) return;

    offersRefreshController?.abort();
    offersRefreshController = new AbortController();

    try {
      const rootUrl = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
      const separator = rootUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${rootUrl}${separator}section_id=${encodeURIComponent(sectionId)}`, {
        signal: offersRefreshController.signal
      });
      if (!response.ok) throw new Error(`Cart offers refresh failed (${response.status})`);

      const sectionHtml = await response.text();
      updateOffersPanel({ sections: { [sectionId]: sectionHtml } });
      scheduleNormalization();
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  };

  const syncOffersFromCart = (event) => {
    if (event?.source === 'foodfor-subscription' || event?.detail?.source === 'foodfor-subscription') {
      scheduleNormalization();
      return;
    }
    const cart = event?.cart || event?.detail?.cart;
    if (cart?.sections) updateOffersPanel(cart);
    else refreshOffersPanel();
    scheduleNormalization();
  };

  const publishCartUpdate = async (cart, key, renderedStateIsCurrent = () => true) => {
    try {
      await refreshCartDrawer();
      if (!renderedStateIsCurrent()) {
        throw new Error('Cart drawer returned stale subscription markup');
      }
    } catch (error) {
      console.warn('FoodFor: AJAX cart rendering failed; reloading the page with the cart drawer reopened.', error);
      await reloadWithCartDrawerOpen();
      return;
    }
    const eventCart = { ...cart };
    delete eventCart.sections;
    if (window.theme?.pubsub?.publish && window.theme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'foodfor-subscription',
        cart: eventCart,
        line: String(key || '')
      });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart: eventCart, source: 'foodfor-subscription' }
    }));
    scheduleNormalization();
  };

  const setSubscriptionBusy = (widget, busy) => {
    if (!widget) return;
    widget.querySelectorAll('button,select').forEach((control) => {
      control.disabled = busy;
    });
    widget.querySelectorAll('button').forEach((button) => {
      button.toggleAttribute('aria-busy', busy);
    });
  };

  const setAllSubscriptionBusy = (busy) => {
    document.querySelectorAll('[data-foodfor-subscription-widget]')
      .forEach((widget) => setSubscriptionBusy(widget, busy));
  };

  const showSubscriptionError = (widget, error) => {
    console.error(error);
    const status = widget.querySelector('[data-foodfor-subscription-status]');
    if (status) status.textContent = widget.dataset.foodforError || 'Cart update failed.';
  };

  let subscriptionMutationInFlight = false;

  const subscribeOne = async (button) => {
    const widget = button.closest('[data-foodfor-subscription-widget]');
    const select = widget?.querySelector('[data-foodfor-plan-select]');
    if (!widget?.dataset.key || !select?.value || subscriptionMutationInFlight) return;

    subscriptionMutationInFlight = true;
    setAllSubscriptionBusy(true);
    try {
      const currentCart = await fetchCartState();
      const variantId = widget.dataset.variantId;
      const currentItem = currentCart.items?.find((item) => item.key === widget.dataset.key)
        || currentCart.items?.find((item) => String(item.variant_id) === variantId && !item.selling_plan_allocation);
      if (!currentItem) throw new Error('The selected cart line is no longer available');
      const cart = await changeSellingPlan({
        key: currentItem.key,
        quantity: currentItem.quantity,
        sellingPlan: select.value
      });
      await publishCartUpdate(cart, currentItem.key, () => (
        !document.querySelector(`[data-foodfor-subscription-widget][data-variant-id="${variantId}"]`)
      ));
    } catch (error) {
      showSubscriptionError(widget, error);
    } finally {
      subscriptionMutationInFlight = false;
      setAllSubscriptionBusy(false);
    }
  };

  const subscribeAll = async (button) => {
    const widget = button.closest('[data-foodfor-subscription-widget]');
    const planSelect = widget?.querySelector('[data-foodfor-all-plan-select]');
    if (!widget || !planSelect?.value || subscriptionMutationInFlight) return;

    const targets = Array.from(widget.querySelectorAll('[data-foodfor-subscription-target]'))
      .map((target) => ({
        key: target.dataset.key,
        variantId: target.dataset.variantId,
        quantity: Number(target.dataset.quantity),
        sellingPlans: Array.from(target.querySelectorAll('[data-foodfor-target-plan]')).map((plan) => ({
          name: plan.dataset.planName,
          id: plan.dataset.sellingPlan
        }))
      }))
      .filter((target) => target.key && target.quantity > 0 && target.sellingPlans.length);
    if (!targets.length) return;

    subscriptionMutationInFlight = true;
    setAllSubscriptionBusy(true);
    try {
      let cart = await fetchCartState();
      const preparedTargets = targets.map((target) => {
        const currentItem = cart.items?.find((item) => item.key === target.key)
          || cart.items?.find((item) => String(item.variant_id) === target.variantId && !item.selling_plan_allocation);
        if (!currentItem) return null;
        const selectedPlan = target.sellingPlans.find((plan) => plan.name === planSelect.value);
        if (!selectedPlan?.id) {
          throw new Error(`Selected delivery frequency is unavailable for variant ${target.variantId}`);
        }
        return { ...target, sellingPlan: selectedPlan.id };
      }).filter(Boolean);
      if (!preparedTargets.length) return;

      let lastChangedKey = '';
      for (let index = 0; index < preparedTargets.length; index += 1) {
        const target = preparedTargets[index];
        const currentItem = cart?.items?.find((item) => item.key === target.key)
          || cart?.items?.find((item) => String(item.variant_id) === target.variantId && !item.selling_plan_allocation);
        if (cart && !currentItem) continue;
        const currentKey = currentItem?.key || target.key;
        cart = await changeSellingPlan({
          key: currentKey,
          quantity: currentItem?.quantity || target.quantity,
          sellingPlan: target.sellingPlan
        });
        lastChangedKey = currentKey;
      }
      if (cart) await publishCartUpdate(cart, lastChangedKey, () => (
        !document.querySelector('[data-foodfor-subscribe-all]')
      ));
    } catch (error) {
      showSubscriptionError(widget, error);
    } finally {
      subscriptionMutationInFlight = false;
      setAllSubscriptionBusy(false);
    }
  };

  const handleSubscriptionClick = (event) => {
    const subscribeOneButton = event.target.closest('[data-foodfor-subscribe-one]');
    if (subscribeOneButton) {
      event.preventDefault();
      subscribeOne(subscribeOneButton);
      return;
    }

    const subscribeAllButton = event.target.closest('[data-foodfor-subscribe-all]');
    if (subscribeAllButton) {
      event.preventDefault();
      subscribeAll(subscribeAllButton);
    }
  };

  const handleSubscriptionChange = (event) => {
    const select = event.target.closest('[data-foodfor-plan-select]');
    if (!select) return;
    const price = select.closest('[data-foodfor-subscription-widget]')?.querySelector('[data-foodfor-plan-price]');
    const selectedOption = select.options[select.selectedIndex];
    if (price && selectedOption?.dataset.price) price.textContent = selectedOption.dataset.price;
  };

  let timer;
  const scheduleNormalization = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(normalizeMonkOffer, 60);
  };

  const start = () => {
    scheduleNormalization();
    const cartDrawer = document.querySelector('#CartDrawer') || document.body;
    new MutationObserver(scheduleNormalization).observe(cartDrawer, { childList: true, subtree: true });
    if (window.theme?.pubsub?.subscribe && window.theme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, syncOffersFromCart);
    }
    document.addEventListener('cart:refresh', syncOffersFromCart);
    document.addEventListener('cart:updated', syncOffersFromCart);
    document.addEventListener('click', handleSubscriptionClick);
    document.addEventListener('change', handleSubscriptionChange);

    let reopenTab = '';
    try {
      reopenTab = window.sessionStorage.getItem('foodfor-cart-reopen-tab') || '';
      window.sessionStorage.removeItem('foodfor-cart-reopen-tab');
    } catch (_error) {
      reopenTab = '';
    }
    if (reopenTab && cartDrawer.id === 'CartDrawer') {
      window.requestAnimationFrame(() => {
        cartDrawer.show?.();
        if (reopenTab === 'offers') {
          cartDrawer.querySelector('[aria-controls^="CartOffers-"]')?.click();
        }
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
